import { LAYER_SOURCES } from './LayerFactory';

/**
 * Mirror of LayerFactory for the 3D view: maps the same backend layer configs
 * to Cesium ImageryProviders instead of OL sources.
 *
 * Cesium is accessed lazily through window.Cesium (loaded globally by
 * public/index.html) and never imported — this file must stay loadable in
 * environments without Cesium (e.g. karma tests).
 *
 * Raster layers are mirrored manually instead of using olcs's
 * RasterSynchronizer because our map views use projections beyond
 * EPSG:4326/3857 (e.g. EPSG:3763), which that synchronizer does not support.
 * The providers below always request EPSG:4326 tiles; the OGC server (QGIS
 * Server) reprojects server-side.
 */
class CesiumLayerFactory {
  /**
   * Factory imagery provider creation method.
   * @param {object} options
   * @param {LAYER_SOURCES} options.sourceType
   * @param {object} options.layerConfig Same configuration object given to
   * LayerFactory.createLayer for this layer.
   * @returns {Cesium.ImageryProvider|null} Created provider, or null when the
   * source type is not raster-mirrored in 3D (WFS is handled by the olcs
   * VectorSynchronizer; BING is not supported in the 3D view yet).
   */
  static createImageryProvider({ sourceType, ...layerConfig }) {
    switch (sourceType) {
      case LAYER_SOURCES.OSM:
        return CesiumLayerFactory.createOSMImageryProvider(layerConfig);
      case LAYER_SOURCES.WMS:
        return CesiumLayerFactory.createWMSImageryProvider(layerConfig);
      case LAYER_SOURCES.WFS:
        return null;
      case LAYER_SOURCES.BING:
        console.warn('Camada BING não suportada na vista 3D — será ignorada.');
        return null;
      default:
        throw new Error(`Unsuported layer source type: ${sourceType}`);
    }
  }

  /**
   * Creates an OpenStreetMap imagery provider.
   *
   * @returns {Cesium.OpenStreetMapImageryProvider} OSM imagery provider.
   */
  static createOSMImageryProvider() {
    const Cesium = CesiumLayerFactory.getCesium();

    return new Cesium.OpenStreetMapImageryProvider({
      url: 'https://tile.openstreetmap.org/',
    });
  }

  /**
   * Creates a WMS imagery provider for the same service/params used in 2D.
   * The default GeographicTilingScheme makes Cesium request EPSG:4326 tiles
   * regardless of the 2D view projection.
   *
   * @param {object} options
   * @param {string} options.url Url of the WMS service.
   * @param {object} options.wmsParams WMS request parameters (same object
   * given to ol/source/TileWMS: LAYERS and optionally STYLES).
   * @returns {Cesium.WebMapServiceImageryProvider} WMS imagery provider.
   */
  static createWMSImageryProvider({ url, wmsParams }) {
    const Cesium = CesiumLayerFactory.getCesium();
    const { LAYERS, ...extraParams } = wmsParams;

    return new Cesium.WebMapServiceImageryProvider({
      url,
      layers: LAYERS,
      parameters: {
        VERSION: '1.1.1',
        TRANSPARENT: true,
        FORMAT: 'image/png',
        ...extraParams,
      },
      enablePickFeatures: false,
    });
  }

  /**
   * @returns {object} The global Cesium namespace.
   * @throws {Error} When Cesium is not loaded.
   */
  static getCesium() {
    if (!window.Cesium) {
      throw new Error('Cesium não está carregado (window.Cesium indefinido)');
    }
    return window.Cesium;
  }
}

export default CesiumLayerFactory;
